import { useState } from 'react'
import { Box, Button, Chip, Fab, InputLabel, MenuItem, Paper, Select, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { CloseOutlined } from '@mui/icons-material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { FileUploader } from 'react-drag-drop-files'
import { RenderTooltip } from '../../../../components/utilities/RenderTooltip'
import { LoadingButton } from '@mui/lab'
import { useCreateBinListMutation } from '../../../../features/lists/listsSlice'
import toaster from '../../../../toaster'
import Papa from 'papaparse'
import { useDispatch } from 'react-redux'
import guenoApi from '../../../../services/guenoApi'

function ModalCreateBin({ setShowCreateBin, userMaster, refetchBins }) {
  const { t } = useTranslation(['common', 'messages'])
  const [file, setFile] = useState('')
  const [binProperty, setBinProperty] = useState('')
  const [csvHeaders, setCsvHeaders] = useState([])
  const [invalidHeaders, setInvalidHeaders] = useState(false)
  const [createBinList, { isLoading }] = useCreateBinListMutation()
  const dispatch = useDispatch()

  const userMasterHeaders = userMaster?.binHeaders?.headers
    ? userMaster.binHeaders.headers
        .replace(/'/g, '')
        .trim()
        .split(',')
        .map((h) => h.trim())
    : []

  const userMasterBinProperty = userMaster?.binHeaders?.binProperty

  const handleFileChange = (file) => {
    setFile(file)
    Papa.parse(file, {
      complete: (results) => {
        if (results.data && results.data.length > 0) {
          const headers = results.data[0].map((h) => h.replace(/'/g, '').trim())
          setCsvHeaders(headers)
          if (
            userMasterHeaders.length > 0 &&
            (headers.length !== userMasterHeaders.length || !headers.every((h, i) => h === userMasterHeaders[i]))
          ) {
            setInvalidHeaders(true)
          } else {
            setInvalidHeaders(false)
          }
        }
      },
      preview: 1,
    })
  }

  const headersToShow = userMasterHeaders.length > 0 ? userMasterHeaders : csvHeaders
  const selectDisabled = !!userMasterBinProperty
  const binPropertyValue = userMasterBinProperty || binProperty
  const saveDisabled = !file || !binPropertyValue || invalidHeaders || headersToShow.length === 0

  const handleSubmit = async () => {
    await handleCreateBin(file, headersToShow, binPropertyValue)
  }

  const handleCreateBin = async (file, headers, binProperty) => {
    try {
      const response = await createBinList({
        file,
        headers,
        binProperty,
      }).unwrap()
      toaster.success(response?.message, { autoClose: 5000, closeButton: true })

      dispatch(guenoApi.util.invalidateTags([{ type: 'UserMaster' }]))
      setShowCreateBin(false)
      refetchBins()
      return response
    } catch (error) {
      toaster.error(error?.message, { autoClose: false, closeButton: true })
    }
  }

  return (
    <Box className="modal">
      <Box className="modal-top">
        <Typography variant="title">
          {t('common:create')} {t('common:binList')}
        </Typography>
        <Fab variant="close" onClick={() => setShowCreateBin(false)}>
          <CloseOutlined />
        </Fab>
      </Box>
      <Box className="modal-box">
        <InputLabel>{t('common:Headers')}</InputLabel>
        <TextField value={headersToShow.join(', ')} disabled />

        <Box className="required">
          <InputLabel>{t('common:BIN')}</InputLabel>
          <Typography variant="subtitle3">({t('common:required')})</Typography>
        </Box>

        <Select
          displayEmpty
          value={binPropertyValue}
          onChange={(e) => setBinProperty(e.target.value)}
          disabled={selectDisabled}
        >
          <MenuItem disabled value="">
            <Typography variant="subtitle3">Select BIN</Typography>
          </MenuItem>
          {headersToShow.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
        <Box className="modal-box">
          <Box className="batch-label">
            <Typography variant="title2">{t('file')}</Typography>
            <RenderTooltip title="CSV comma separated values" />
            <Typography size="small" variant="number">
              {t('common:onlyComma')}
            </Typography>
          </Box>
          <FileUploader
            handleChange={handleFileChange}
            name="file"
            label={t('common:BATCH_FILE_UPLOADER_LABEL')}
            types={['csv']}
          >
            <Paper
              elevation={0}
              variant="rootDashed"
              className="draggable-main draggable-small"
              sx={{ minHeight: 'auto' }}
            >
              <Box className="draggable-icon" />
              <Typography variant="title2">{t('common:BATCH_FILE_UPLOADER_LABEL')}</Typography>
              <Box>
                <Typography variant="subtitle4">{t('common:or')} </Typography>
                <Typography sx={{ textDecoration: 'underline' }} variant="subtitle4" color="secondary">
                  {t('common:browseMedia')}
                </Typography>
              </Box>
            </Paper>
          </FileUploader>
          <Typography variant="subtitle4">{t('common:supportedMedia')}: CSV.</Typography>

          {file && (
            <Paper elevation={0} variant="colorPrimary" className="onb-file">
              <Box className="onb-file-description">
                <Box className="color-button" sx={{ background: '#E4DDFF' }}>
                  <Typography variant="subtitle1" sx={{ color: '#7E65D5' }}>
                    CSV
                  </Typography>
                </Box>

                <Typography
                  variant="h6"
                  title={file.name}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  maxWidth="160px"
                >
                  {file.name}
                </Typography>
              </Box>
              <Box className="onb-file-icons">
                <Button variant="outlinedBlank" onClick={() => setFile('')}>
                  <DeleteOutlineIcon />
                </Button>
              </Box>
            </Paper>
          )}

          {invalidHeaders && (
            <Button size="small" variant="outlinedChipError">
              {t('common:invalidCsvHeaders')}
            </Button>
          )}
        </Box>
      </Box>
      <Box className="lists-buttons">
        <LoadingButton size="small" variant="outlinedGrey" onClick={() => setShowCreateBin(false)} loading={isLoading}>
          {t('common:cancel')}
        </LoadingButton>
        <LoadingButton
          disabled={saveDisabled}
          size="small"
          variant="contained"
          type="submit"
          onClick={handleSubmit}
          loading={isLoading}
        >
          {t('common:save')}
        </LoadingButton>
      </Box>
    </Box>
  )
}

export default ModalCreateBin
